import { Text } from "@mantine/core";
import { useDisclosure } from "@mantine/hooks";
import { Fragment, useCallback } from "react";

import { useTheme } from "../../../hooks";
import {
  LogbooksSection,
  SectionWrapper,
} from "../../../pages/Home/Home.styled";
import { type TLogbookStatus } from "../../../pages/Home/Home.types";
import LogbookCard from "../../shared/LogbookCard/LogbookCard";
import LoginModal from "./LoginModal";

interface Props {
  data: any;
  status: TLogbookStatus;
  search?: string;
}

const LogbooksList = ({ data, status, search = "" }: Props) => {
  const theme = useTheme();
  const [opened, { open, close }] = useDisclosure();

  // Custom sort function to handle logbooks
  const sortLogbooks = (logbooks: any[]) => {
    return [...logbooks].sort((a, b) => {
      // First sort by geLockedLog status (false comes first)
      if (a) {
        if (a.geLockedLog === false && b.geLockedLog === true) return -1;
        if (a.geLockedLog === true && b.geLockedLog === false) return 1;

        // Then sort alphabetically by name
        const nameA = a.lbname?.toLowerCase() || "";
        const nameB = b.lbname?.toLowerCase() || "";
        return nameA.localeCompare(nameB);
      }
    });
  };

  const renderLogbookCards = useCallback(
    (el: any, index: number) => {
      if (el.logbooks.length === 0) return null;
      let logbooks: any[] = [];
      // check for null logbooks, occcurs when logbooks are grouped but unticked via settings
      if (el.logbooks.length > 0 && el.logbooks instanceof Array) {
        el.logbooks.map((v: any, k: any) => {
          if (v) {
            logbooks.push(v);
          }
        });
      }
      const filteredLogbooks = search
        ? logbooks.filter(logbook =>
            logbook?.lbname?.toLowerCase().includes(search.toLowerCase()),
          )
        : logbooks;

      const sortedLogbooks = sortLogbooks(filteredLogbooks);

      if (sortedLogbooks.length === 0) return null;

      return (
        <SectionWrapper key={index}>
          <Text mb={15} size="lg" fw={600}>
            {el.group !== "Others" && (
              <Text color={el.color ?? theme.colors.main[9]}>{el.group}</Text>
            )}
          </Text>
          <LogbooksSection
            color={
              el.group !== "Others"
                ? (el.color ?? theme.colors.main[9])
                : undefined
            }>
            {sortedLogbooks.map(logbook => (
              <LogbookCard
                key={logbook?.lbid}
                {...logbook}
                open={logbook?.open ?? 0}
                lastentryDate={logbook?.lastentry_date ?? ""}
                lastentryTime={logbook?.lastentry_time ?? ""}
                showPreview={status === "available"}
                status={status}
                openAuthModal={open}
              />
            ))}
          </LogbooksSection>
        </SectionWrapper>
      );
    },
    [search, status, theme.colors.main, open],
  );

  return (
    <Fragment>
      {data?.[status as any]?.map(renderLogbookCards)}
      <LoginModal close={close} opened={opened} />
    </Fragment>
  );
};

export default LogbooksList;
